import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { inventoryMakesWithCounts } from "@/lib/carCounts";
import api from "@/lib/api";
import { SITE_CONTAINER } from "@/lib/siteLayout";

function brandHref(make) {
  return `/?marca=${encodeURIComponent(make)}#keszlet`;
}

function BrandLink({ make, count, duplicate = false }) {
  const label = `${make} (${count})`;
  return (
    <Link
      to={brandHref(make)}
      title={`${make} — ${count} használt autó a készletben`}
      data-testid={duplicate ? undefined : "brand-marquee-link"}
      className="group shrink-0 inline-flex items-center gap-2 whitespace-nowrap"
    >
      <span className="text-sm md:text-base font-medium text-slate-400 group-hover:text-electric transition-colors">
        {make}
      </span>
      <span className="text-xs md:text-sm font-semibold tabular-nums text-electric/80 group-hover:text-electric bg-electric/10 group-hover:bg-electric/20 px-2 py-0.5 rounded-full transition-colors">
        {count}
      </span>
      <span className="text-slate-600 ml-4 select-none" aria-hidden="true">·</span>
    </Link>
  );
}

/** Páros számú teljes sorozat — a CSS -50% animáció zökkenőmentes legyen ultrawide-on is. */
function marqueeCopyCount(itemCount) {
  const n = Math.max(itemCount, 1);
  let copies = Math.max(4, Math.ceil(24 / n) * 2);
  if (copies % 2 !== 0) copies += 1;
  return copies;
}

function MarqueeRow({ items, reverse = false, copies = 4 }) {
  const sequence = useMemo(() => {
    const base = reverse ? [...items].reverse() : items;
    return Array.from({ length: copies }, () => base).flat();
  }, [items, reverse, copies]);
  const segmentLen = items.length;

  return (
    <div
      className={`brand-marquee-row flex w-max flex-nowrap gap-6 ${reverse ? "brand-marquee-row--reverse" : ""}`}
    >
      {sequence.map(({ make, count }, i) => (
        <BrandLink
          key={`${reverse ? "r" : "f"}-${make}-${i}`}
          make={make}
          count={count}
          duplicate={i >= segmentLen}
        />
      ))}
    </div>
  );
}

export function BrandMarquee() {
  const [items, setItems] = useState({ rows: [], total: 0 });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const carsRes = await api.get("/cars");
        if (cancelled) return;
        const list = carsRes.data || [];
        setItems({ rows: inventoryMakesWithCounts(list), total: list.length });
      } catch (_) {
        if (!cancelled) setItems({ rows: [], total: 0 });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const brandRows = items.rows || [];
  if (!brandRows.length) return null;

  const total = items.total ?? brandRows.reduce((sum, { count }) => sum + count, 0);
  const copies = marqueeCopyCount(brandRows.length);

  return (
    <section
      data-testid="brand-marquee"
      aria-labelledby="brand-marquee-heading"
      className="border-t border-white/10 bg-anthracite-800/40 overflow-hidden"
    >
      <div className={`${SITE_CONTAINER} pt-8 pb-2`}>
        <h2 id="brand-marquee-heading" className="text-center text-xs tracking-[0.25em] uppercase text-slate-500 mb-2">
          Használt autók márkánként
        </h2>
        <p className="text-center text-sm text-slate-400 mb-5">
          Kattints a márkára — <span className="text-electric font-medium">{total} autó</span> érhető el összesen
        </p>
      </div>

      <div className="brand-marquee-mask relative pb-8">
        <div className="brand-marquee-track flex flex-col gap-4">
          <MarqueeRow items={brandRows} copies={copies} />
          <MarqueeRow items={brandRows} copies={copies} reverse />
        </div>

        <nav aria-label="Autómárkák a készletben" className="sr-only">
          <ul>
            {brandRows.map(({ make, count }) => (
              <li key={make}>
                <Link to={brandHref(make)}>
                  {make} használt autó — {count} db a készletben
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </section>
  );
}
